import React from 'react';
import IconButton from '@mui/material/IconButton';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import Autocomplete from '@/components/form/Autocomplete';
import SvgSearch from './SvgSearch';
import SvgClear from './SvgClear';
import { OptionType } from '@/types/common';
import { SEARCH_MODE_TYPE } from '@/constants/common';
import { BASE_HEADER_HEIGHT } from '@/constants/profile-builder';
import { SearchType } from '@/types/job-title';
import { useHasBeenSeen } from '@/utils/hooks/useHasBeenSeen';
import { sendEvent } from '@/utils/site-track';
import { searchByQuery } from '@/api/tanzhi';

const SearchBox = (props: {
  initValue?: SearchType;
  onChange?: (value: SearchType) => void;
  enableAnimation?: boolean;
  placeholder?: string;
  noOptionsText?: string;
  mode?: string;
  className?: string;
  inputClassName?: string;
  inputBorderColor?: string;
  showEndAdornment?: boolean;
}) => {
  const {
    initValue = null,
    onChange,
    enableAnimation = false,
    placeholder = '搜索',
    noOptionsText = '抱歉，暂未搜索到相关数据',
    mode = SEARCH_MODE_TYPE.NAME_SEARCH,
    className = 'w-64',
    inputBorderColor = 'border border-web-grey-50',
    inputClassName = '!bg-white !h-12 pl-5 pr-3',
    showEndAdornment = true,
  } = props;
  const [searchValue, setSearchValue] = React.useState<SearchType | string>(
    initValue,
  );
  const [expanded, setExpanded] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (value: SearchType | string) => {
    if (!value || typeof value === 'string') return;
    const { id, name, search_type } = value;
    if (!search_type || !id) return;
    setSearchValue(value);
    onChange?.(value);
    typeof searchValue !== 'string' &&
      id !== searchValue?.id &&
      sendEvent({
        name: `search_${search_type}`,
        search_keyword: name,
      });
  };

  const handleBlur = () => {
    (document.activeElement as HTMLElement)?.blur();
  };

  useHasBeenSeen(inputRef, () => {}, handleBlur, -BASE_HEADER_HEIGHT);

  return (
    <div ref={inputRef} className="relative flex items-center justify-end">
      <Autocomplete
        className={`transition-all duration-300 ease-out origin-right ${className} ${!enableAnimation || expanded ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
        inputClassName={`rounded-lg ${inputClassName} ${inputBorderColor}`}
        value={searchValue}
        openAtFirstClick={false}
        getOptions={(query) => searchByQuery(query, mode)}
        noOptionsText={noOptionsText}
        placeholder={placeholder}
        getOptionLabel={(item: OptionType | string) =>
          typeof item === 'string' ? item : item.name
        }
        onChange={handleChange}
        isOptionEqualToValue={(option, value) =>
          typeof value === 'string' || typeof option === 'string'
            ? option === value
            : option?.id === value?.id
        }
        freeSolo={true}
        disableUnderline={true}
        startAdornment={<SvgSearch className="text-primary" />}
        endAdornment={
          showEndAdornment && (
            <IconButton
              size="medium"
              onClick={(e) => {
                e.stopPropagation();
                enableAnimation && setExpanded(false);
                handleChange(null);
                handleBlur();
              }}
            >
              <SvgClear />
            </IconButton>
          )
        }
        selectOnFocus={false}
        clearValue={true}
        renderOption={(props, option, { inputValue }) => {
          if (typeof option === 'string') return;
          const { key, ...optionProps } = props;
          const matches = match(option.name, inputValue, { insideWords: true });
          const parts = parse(option.name, matches);
          return (
            <li key={key} {...optionProps}>
              {parts.map((part, index) => (
                <span
                  key={index}
                  className={part.highlight ? 'font-semibold' : ''}
                >
                  {part.text}
                </span>
              ))}
            </li>
          );
        }}
        clearOnBlur={false}
      />
      {enableAnimation && !expanded && (
        <div
          className="p-3 bg-web-grey-100 rounded-lg cursor-pointer z-1 group hover:bg-web-neutral-300"
          onClick={() => setExpanded(true)}
        >
          <SvgSearch className="text-web-grey-550 group-hover:text-primary" />
        </div>
      )}
    </div>
  );
};

export default SearchBox;
